import { db } from "../../../../../firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  increment,
} from "firebase/firestore";

/**
 * Toggle like for a project
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Like status
 */
export const toggleProjectLike = async (projectId, userId) => {
  try {
    if (!projectId || !userId) throw new Error("Missing projectId or userId");
    const likeRef = doc(db, "projectLikes", `${projectId}_${userId}`);
    const projectRef = doc(db, "projects", projectId);
    const likeDoc = await getDoc(likeRef);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) throw new Error("Project not found");

    const isLiked = likeDoc.exists();
    const current = projectDoc.data()?.likeCount || 0;

    const batch = writeBatch(db);
    if (isLiked) {
      batch.delete(likeRef);
      batch.update(projectRef, { likeCount: Math.max(0, current - 1) });
    } else {
      batch.set(likeRef, { projectId, userId, timestamp: serverTimestamp() });
      batch.update(projectRef, { likeCount: current + 1 });
    }
    await batch.commit();

    const likeCount = isLiked ? Math.max(0, current - 1) : current + 1;
    return { isLiked: !isLiked, likes: likeCount };
  } catch (e) {
    console.error("Error toggling project like:", e);
    throw e;
  }
};

/**
 * Toggle follow for a project
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Follow status
 */
export const toggleProjectFollow = async (projectId, userId) => {
  try {
    if (!projectId || !userId) throw new Error("Missing projectId or userId");
    const followRef = doc(db, "projectFollows", `${projectId}_${userId}`);
    const projectRef = doc(db, "projects", projectId);
    const followDoc = await getDoc(followRef);
    const projectDoc = await getDoc(projectRef);
    if (!projectDoc.exists()) throw new Error("Project not found");

    const isFollowed = followDoc.exists();
    const current = projectDoc.data()?.followerCount || 0;
    const batch = writeBatch(db);
    if (isFollowed) {
      batch.delete(followRef);
      batch.update(projectRef, { followerCount: Math.max(0, current - 1) });
    } else {
      batch.set(followRef, { projectId, userId, timestamp: serverTimestamp() });
      batch.update(projectRef, { followerCount: current + 1 });
    }
    await batch.commit();

    const followerCount = isFollowed ? Math.max(0, current - 1) : current + 1;
    return { isFollowed: !isFollowed, followers: followerCount };
  } catch (e) {
    console.error("Error toggling project follow:", e);
    throw e;
  }
};

/**
 * Increment share count for a project
 * @param {string} projectId - Project ID
 */
export const incrementProjectShare = async (projectId) => {
  try {
    if (!projectId) throw new Error("Missing projectId");
    const projectRef = doc(db, "projects", projectId);
    await updateDoc(projectRef, { shareCount: increment(1) });
    return { shares: undefined };
  } catch (e) {
    console.error("Error incrementing project share:", e);
    throw e;
  }
};

/**
 * Increment updates count for a project
 * @param {string} projectId - Project ID
 */
export const incrementProjectUpdates = async (projectId) => {
  try {
    if (!projectId) throw new Error("Missing projectId");
    const projectRef = doc(db, "projects", projectId);
    await updateDoc(projectRef, {
      updatesCount: increment(1),
      updatedAt: serverTimestamp(),
    });
    return { updates: undefined };
  } catch (e) {
    console.error("Error incrementing project updates:", e);
    throw e;
  }
};
